// src/admin/pages/AdminAnketa.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { anketaService } from "../../../services/api";

const PageContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const PageTitle = styled.h1`
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  color: var(--secondary-color);
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.8rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background-color: #f8f8f8;
    font-weight: 600;
  }

  tr:hover {
    background-color: #f9f9f9;
  }

  @media (max-width: 768px) {
    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
    }

    tr {
      margin-bottom: 1rem;
      border: 1px solid #ddd;
      border-radius: 4px;
      overflow: hidden;
    }

    td {
      display: flex;
      justify-content: space-between;
      border: none;
      border-bottom: 1px solid #eee;

      &:last-child {
        border-bottom: none;
      }

      &:before {
        content: attr(data-label);
        font-weight: 600;
      }
    }
  }
`;

const ActionLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  cursor: pointer;
  margin-right: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const DangerActionLink = styled.a`
  color: var(--error-color);
  text-decoration: none;
  cursor: pointer;
  margin-right: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: var(--error-color);
  }
`;

const ModalTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: var(--secondary-color);
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.h4`
  margin-bottom: 0.3rem;
  color: #666;
`;

const DetailValue = styled.p`
  margin: 0;
  font-size: 1.1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const AdminAnketa = () => {
  const [anketaList, setAnketaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAnketa, setSelectedAnketa] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchAnketaList = async () => {
      try {
        setLoading(true);
        const response = await anketaService.getAll();
        setAnketaList(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching anketa list:", err);
        setError("Не вдалося завантажити список анкет");
        setLoading(false);
      }
    };

    fetchAnketaList();
  }, []);

  const openDetails = (anketa) => {
    setSelectedAnketa(anketa);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAnketa(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цю анкету?")) {
      return;
    }

    try {
      await anketaService.delete(id);
      setAnketaList(anketaList.filter((anketa) => anketa.id !== id));
    } catch (err) {
      console.error("Error deleting anketa:", err);
      alert("Помилка при видаленні анкети");
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageTitle>Анкети на вступ</PageTitle>
        <LoadingContainer>
          <p>Завантаження...</p>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitle>Анкети на вступ</PageTitle>
        <p>{error}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>Анкети на вступ</PageTitle>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Ім'я</th>
              <th>Рік народження</th>
              <th>Телефон</th>
              <th>Дія</th>
              <th>Створено</th>
            </tr>
          </thead>
          <tbody>
            {anketaList.map((anketa) => (
              <tr key={anketa.id} style={{ fontWeight: anketa.wasRead ? "normal" : "bold" }}>
                <td data-label="Ім'я">{anketa.name}</td>
                <td data-label="Рік народження">{anketa.birth}</td>
                <td data-label="Телефон">
                  <a href={`tel:${anketa.tel}`}>{anketa.tel}</a>
                </td>
                <td data-label="Дія">
                  <ActionLink
                    onClick={async () => {
                      await openDetails(anketa);

                      if (!anketa.wasRead) {
                        try {
                          await anketaService.markAsRead(anketa.id);
                          setAnketaList((prev) =>
                            prev.map((a) => (a.id === anketa.id ? { ...a, wasRead: true } : a)),
                          );
                        } catch (err) {
                          console.error("Failed to mark as read:", err);
                        }
                      }
                    }}
                  >
                    Деталі
                  </ActionLink>
                  <DangerActionLink onClick={() => handleDelete(anketa.id)}>Вилучити</DangerActionLink>
                </td>
                <td data-label="Створено">{anketa.created_at.replace("T", " ").substring(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {modalOpen && selectedAnketa && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>×</CloseButton>
            <ModalTitle>Деталі анкети</ModalTitle>

            <DetailItem>
              <DetailLabel>Ім'я</DetailLabel>
              <DetailValue>{selectedAnketa.name}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Рік народження</DetailLabel>
              <DetailValue>{selectedAnketa.birth}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Контактний номер телефону</DetailLabel>
              <DetailValue>
                <a href={`tel:${selectedAnketa.tel}`}>{selectedAnketa.tel}</a>
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Досвід у силових структурах</DetailLabel>
              <DetailValue>{selectedAnketa.expInStructure == 1 ? "Так" : "Ні"}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Бойовий досвід</DetailLabel>
              <DetailValue>{selectedAnketa.expInBattle == 1 ? "Так" : "Ні"}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Досвід зі зброєю</DetailLabel>
              <DetailValue>{selectedAnketa.expWithGuns == 1 ? "Так" : "Ні"}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Водійське посвідчення, категорія</DetailLabel>
              <DetailValue>{selectedAnketa.driver || "Немає"}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Примітки</DetailLabel>
              <DetailValue>{selectedAnketa.notes || "Немає"}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Створено</DetailLabel>
              <DetailValue>{selectedAnketa.created_at}</DetailValue>
            </DetailItem>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default AdminAnketa;
